// @flow

/**
 * Markdown to DOCX Conversion Utilities
 * Converts markdown text to docx document structure
 */

import {
  Document,
  Paragraph,
  TextRun,
  ImageRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  LevelFormat,
} from 'docx';

type TextStyle = {
  bold?: boolean,
  italics?: boolean,
  code?: boolean,
};

/**
 * Parse inline markdown formatting (bold, italic, code)
 * Returns array of TextRun objects.
 * Optional extraProps are spread into every TextRun (e.g., size/bold for headings).
 */
const DOCX_DEFAULT_FONT = 'Arial';

const parseInlineFormatting = (text: string, extraProps?: Object): TextRun[] => {
  const runs: TextRun[] = [];
  const base = { font: DOCX_DEFAULT_FONT, ...(extraProps || {}) };

  // Regex to match **bold**, *italic*, `code`
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;

  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      runs.push(new TextRun({ ...base, text: text.slice(lastIndex, match.index) }));
    }

    // Add formatted text
    if (match[2]) {
      // Bold **text**
      runs.push(new TextRun({ ...base, text: match[2], bold: true }));
    } else if (match[3]) {
      // Italic *text*
      runs.push(new TextRun({ ...base, text: match[3], italics: true }));
    } else if (match[4]) {
      // Code `text` — specific props (font) override extraProps
      runs.push(new TextRun({
        ...base,
        text: match[4],
        font: 'Courier New',
        shading: { fill: 'E8E8E8' },
      }));
    }

    lastIndex = regex.lastIndex;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    runs.push(new TextRun({ ...base, text: text.slice(lastIndex) }));
  }

  // If no formatting found, return the whole text
  if (runs.length === 0) {
    runs.push(new TextRun({ ...base, text }));
  }

  return runs;
};

/**
 * Detect heading level from markdown line
 * Returns null if not a heading
 */
const parseHeading = (line: string): { level: number, text: string } | null => {
  const match = line.match(/^(#{1,6})\s+(.+)$/);
  if (match) {
    return { level: match[1].length, text: match[2] };
  }
  return null;
};

/**
 * Parse markdown table into rows and cells
 */
const parseTable = (lines: string[], startIndex: number): { table: string[][], endIndex: number } => {
  const rows: string[][] = [];
  let i = startIndex;
  
  while (i < lines.length) {
    const line = lines[i].trim();
    
    // Check if line is a table row (starts and ends with |, or contains |)
    if (!line.includes('|')) {
      break;
    }
    
    // Skip delimiter row (e.g., |---|---|)
    if (/^\|?\s*[-:]+\s*\|/.test(line)) {
      i++;
      continue;
    }
    
    // Parse cells
    const cells = line
      .split('|')
      .map(cell => cell.trim())
      .filter((cell, idx, arr) => !(idx === 0 && cell === '') && !(idx === arr.length - 1 && cell === ''));
    
    if (cells.length > 0) {
      rows.push(cells);
    }
    
    i++;
  }
  
  return { table: rows, endIndex: i };
};

/**
 * Create a DOCX Table from parsed table data
 */
const createDocxTable = (tableData: string[][], isHeader: boolean = true): Table => {
  const numCols = tableData[0]?.length || 1;
  // Usable page width ≈ 9360 twips (6.5 inches at 1440 twips/inch)
  const colWidth = Math.floor(9360 / numCols);

  const rows = tableData.map((rowCells, rowIndex) => {
    const cells = rowCells.map(cellText =>
      new TableCell({
        children: [new Paragraph({ children: parseInlineFormatting(cellText) })],
        shading: rowIndex === 0 && isHeader ? { fill: 'E8E8E8' } : undefined,
        width: { size: colWidth, type: WidthType.DXA },
      })
    );
    return new TableRow({ children: cells });
  });

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    columnWidths: Array(numCols).fill(colWidth),
  });
};

/**
 * Convert markdown string to DOCX Document
 */
// Heading sizes in half-points (e.g., 48 = 24pt)
const DOCX_HEADING_SIZES = { 1: 48, 2: 40, 3: 32, 4: 28, 5: 24, 6: 22 };

/**
 * Convert a data URL to an ArrayBuffer for docx ImageRun
 */
function dataUrlToArrayBuffer(dataUrl: string): ArrayBuffer {
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let j = 0; j < binary.length; j++) {
    bytes[j] = binary.charCodeAt(j);
  }
  return bytes.buffer;
}

export const markdownToDocx = (markdown: string, chartImages?: { [string]: string }): Document => {
  const lines = markdown.split('\n');
  const children: (Paragraph | Table)[] = [];

  let i = 0;
  let inCodeBlock = false;
  let codeBlockLines: string[] = [];
  
  while (i < lines.length) {
    const line = lines[i];
    
    // Handle code blocks
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        // End code block
        children.push(new Paragraph({
          children: [new TextRun({ 
            text: codeBlockLines.join('\n'),
            font: 'Courier New',
          })],
          shading: { fill: 'F5F5F5' },
        }));
        codeBlockLines = [];
        inCodeBlock = false;
      } else {
        // Start code block
        inCodeBlock = true;
      }
      i++;
      continue;
    }
    
    if (inCodeBlock) {
      codeBlockLines.push(line);
      i++;
      continue;
    }
    
    // Handle empty lines
    if (line.trim() === '') {
      children.push(new Paragraph({ children: [] }));
      i++;
      continue;
    }
    
    // Handle headings
    const heading = parseHeading(line);
    if (heading) {
      const headingMap = {
        1: HeadingLevel.HEADING_1,
        2: HeadingLevel.HEADING_2,
        3: HeadingLevel.HEADING_3,
        4: HeadingLevel.HEADING_4,
        5: HeadingLevel.HEADING_5,
        6: HeadingLevel.HEADING_6,
      };
      children.push(new Paragraph({
        children: parseInlineFormatting(heading.text, {
          bold: true,
          size: DOCX_HEADING_SIZES[heading.level] || 28,
        }),
        heading: headingMap[heading.level] || HeadingLevel.HEADING_1,
        spacing: { before: 240, after: 120 }, // 12pt before, 6pt after
      }));
      i++;
      continue;
    }
    
    // Handle tables
    if (line.includes('|')) {
      const { table, endIndex } = parseTable(lines, i);
      if (table.length > 0) {
        children.push(createDocxTable(table));
        i = endIndex;
        continue;
      }
    }
    
    // Handle unordered lists
    const ulMatch = line.match(/^(\s*)[-*+]\s+(.+)$/);
    if (ulMatch) {
      children.push(new Paragraph({
        children: parseInlineFormatting(ulMatch[2]),
        bullet: { level: Math.floor(ulMatch[1].length / 2) },
      }));
      i++;
      continue;
    }
    
    // Handle ordered lists
    const olMatch = line.match(/^(\s*)\d+\.\s+(.+)$/);
    if (olMatch) {
      children.push(new Paragraph({
        children: parseInlineFormatting(olMatch[2]),
        numbering: { reference: 'default-numbering', level: Math.floor(olMatch[1].length / 2) },
      }));
      i++;
      continue;
    }
    
    // Handle blockquotes
    const bqMatch = line.match(/^>\s*(.*)$/);
    if (bqMatch) {
      children.push(new Paragraph({
        children: parseInlineFormatting(bqMatch[1]),
        indent: { left: 720 }, // 0.5 inch
        border: {
          left: { style: BorderStyle.SINGLE, size: 12, color: 'CCCCCC' },
        },
      }));
      i++;
      continue;
    }
    
    // Handle chart image tokens: {{CHART_IMAGE:displayId}}
    const chartMatch = line.trim().match(/^\{\{CHART_IMAGE:(.+?)\}\}$/);
    if (chartMatch && chartImages && chartImages[chartMatch[1]]) {
      const imageData = dataUrlToArrayBuffer(chartImages[chartMatch[1]]);
      children.push(new Paragraph({
        children: [
          new ImageRun({
            data: imageData,
            transformation: { width: 600, height: 300 },
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { before: 160, after: 240 },
      }));
      i++;
      continue;
    }

    // Default: regular paragraph
    children.push(new Paragraph({
      children: parseInlineFormatting(line),
    }));
    i++;
  }
  
  return new Document({
    numbering: {
      config: [{
        reference: 'default-numbering',
        levels: [
          {
            level: 0, format: LevelFormat.DECIMAL, text: '%1.', alignment: AlignmentType.START, start: 1,
            style: { paragraph: { indent: { left: 720, hanging: 360 } } },
          },
          {
            level: 1, format: LevelFormat.LOWER_LETTER, text: '%2.', alignment: AlignmentType.START, start: 1,
            style: { paragraph: { indent: { left: 1440, hanging: 360 } } },
          },
          {
            level: 2, format: LevelFormat.LOWER_ROMAN, text: '%3.', alignment: AlignmentType.START, start: 1,
            style: { paragraph: { indent: { left: 2160, hanging: 360 } } },
          },
        ],
      }],
    },
    sections: [{
      children,
    }],
  });
};
